"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@/components/icons";
import { useDarkColors } from "@/hooks/useDarkColors";
import { useTranslator } from "@/lang";
import { useAuthStore } from "@/stores/authStore";
import { useLoginModalStore } from "@/stores/loginModalStore";
import { usePortalStore } from "@/stores/portalStore";
import { useStackNavigation } from "@/hooks/useStackNavigation";
import {
  registerTopPriorityBackInterceptor,
  unregisterTopPriorityBackInterceptor,
} from "@/stores/navigationStackStore";
import { getRelationships, type RelationshipItem } from "@/api/relationshipApi";
import { isUniverseNew, UNIVERSE_VISITED_EVENT } from "@/utils/museUniverseBadge";
import { getAssetUrl } from "@/utils/assetUrl";
import { getLocalizedText } from "@/utils/getLocalizedText";
import SignupRewardInfo from "@/components/SignupRewardInfo/SignupRewardInfo";
import {
  TEMP_TITLE_EXTRABOLD_XL,
  TEMP_TEXT_BOLD_L,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_SEMIBOLD_M,
  TEMP_TEXT_SEMIBOLD_XS,
  TEMP_TEXT_BOLD_XS,
} from "@/constants/tempTypo";

// ============================================================
// 공통 — 친밀도 단계 / 레이아웃 / 데이터 훅
// ============================================================

// 친밀도/여자친구 → 관계 단계 라벨 + 대표 색 (IntimacyInfoModal 단계와 동일 체계)
// label은 i18n 키 — 렌더 시점에 t() 적용
function getStage(
  intimacy: number,
  isGirlfriend: boolean,
): { label: string; color: string } {
  if (isGirlfriend) return { label: "galaxy.stage.girlfriend", color: "#FE375B" };
  if (intimacy <= 20) return { label: "galaxy.stage.awkward", color: "#9e9e9e" };
  if (intimacy <= 40) return { label: "galaxy.stage.gettingCloser", color: "#FFB07A" };
  if (intimacy <= 70) return { label: "galaxy.stage.fallingHard", color: "#FF6B8A" };
  if (intimacy <= 90) return { label: "galaxy.stage.secret", color: "#F75555" };
  return { label: "galaxy.stage.perfectBond", color: "#FE375B" };
}

const GOLDEN_ANGLE = 137.50776; // deg — 노드를 고르게 흩뿌리는 황금각
const EMPTY_ITEMS: RelationshipItem[] = [];
const MEDAL_COLORS = ["#FFD24A", "#C7CBD1", "#E8A06A"];

// 관계 단계 정의 (getStage 라벨 키와 1:1 매칭) — 누적 분포 막대용
const STAGE_DEFS = [
  { label: "galaxy.stage.girlfriend", color: "#FE375B" },
  { label: "galaxy.stage.perfectBond", color: "#FE375B" },
  { label: "galaxy.stage.secret", color: "#F75555" },
  { label: "galaxy.stage.fallingHard", color: "#FF6B8A" },
  { label: "galaxy.stage.gettingCloser", color: "#FFB07A" },
  { label: "galaxy.stage.awkward", color: "#9e9e9e" },
];

/** 모든 관계 화면이 공유하는 쿼리 (캐시 공유 → 화면 전환 시 재요청 없음) */
export function useRelationships() {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  return useQuery({
    queryKey: ["relationships"],
    queryFn: getRelationships,
    enabled: isLoggedIn,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

interface Positioned {
  item: RelationshipItem;
  x: number; // px (캔버스 좌표)
  y: number; // px
  size: number; // px
  stage: ReturnType<typeof getStage>;
}

// 친밀도 높을수록 중심에 가깝게. 캐릭터 수가 많을수록 캔버스를 키워 패닝 가능하게.
function buildLayout(items: RelationshipItem[]): {
  positions: Positioned[];
  canvas: number;
  center: number;
} {
  const n = items.length;
  const minR = 84;
  const maxR = Math.min(380, 132 + Math.max(0, n - 5) * 15);
  const canvas = Math.round((maxR + 78) * 2);
  const center = canvas / 2;

  const positions = items.map((item, i) => {
    const intimacy = Math.min(100, Math.max(0, item.intimacy));
    const rNorm = 1 - intimacy / 100;
    const jitter = ((i % 3) - 1) * 17;
    const radius = Math.max(58, minR + rNorm * (maxR - minR) + jitter);
    const angle = (i * GOLDEN_ANGLE + 34) * (Math.PI / 180);
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    const size = 46 + (intimacy / 100) * 18;
    return {
      item,
      x,
      y,
      size,
      stage: getStage(item.intimacy, item.isGirlfriend),
    };
  });

  return { positions, canvas, center };
}

// 배경 별 — 하드코딩(SSR 하이드레이션 mismatch 방지)
const STARS = [
  { top: "12%", left: "18%", s: 3, d: 0 },
  { top: "8%", left: "72%", s: 2, d: 0.6 },
  { top: "22%", left: "46%", s: 2, d: 1.2 },
  { top: "30%", left: "86%", s: 3, d: 0.3 },
  { top: "42%", left: "10%", s: 2, d: 1.5 },
  { top: "54%", left: "90%", s: 2, d: 0.9 },
  { top: "64%", left: "24%", s: 3, d: 0.2 },
  { top: "70%", left: "64%", s: 2, d: 1.1 },
  { top: "78%", left: "40%", s: 2, d: 0.7 },
  { top: "84%", left: "82%", s: 3, d: 1.4 },
  { top: "18%", left: "60%", s: 2, d: 1.8 },
  { top: "88%", left: "14%", s: 2, d: 0.5 },
  { top: "36%", left: "70%", s: 2, d: 2.0 },
  { top: "60%", left: "50%", s: 2, d: 1.3 },
];

// ============================================================
// 1) 나의 세계 — 궤도 성좌 (스택 스크린)
// ============================================================

// 성좌 캔버스 핀치줌/팬 제스처
type ConstellationGesture =
  | {
      type: "pinch";
      startDist: number;
      startScale: number;
      px: number;
      py: number;
      cx: number;
      cy: number;
    }
  | {
      type: "pan";
      startX: number;
      startY: number;
      startTx: number;
      startTy: number;
    };

export function RelationshipWorld({
  onBackPress,
}: {
  onBackPress: () => void;
}) {
  const c = useDarkColors();
  const { t, f, language } = useTranslator();
  const { push } = useStackNavigation();
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useLoginModalStore((s) => s.open);
  const openConfirm = usePortalStore((s) => s.openConfirm);

  // 캐릭터 노드 탭 → 기억 우주(/muse-universe)로 진입.
  // 기존 상세(속마음) 바텀시트는 기억 우주 화면 안의 "마음" 버튼으로 이동했다.

  const { data, isLoading } = useRelationships();
  const items = data?.items ?? EMPTY_ITEMS;
  const { positions, canvas, center } = useMemo(
    () => buildLayout(items),
    [items],
  );

  // 기억 우주 NEW 배지 — 마지막 방문 후 실제로 새 기억 별이 생긴 뮤즈
  const [universeNewIds, setUniverseNewIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const compute = () =>
      setUniverseNewIds(
        new Set(
          items
            .filter((i) =>
              isUniverseNew(i.characterId, {
                lastMessageAt: i.lastMessageAt,
                chatCount: i.chatCount,
                activeMemoryCount: i.activeMemoryCount,
                latestMemoryAt: i.latestMemoryAt,
              }),
            )
            .map((i) => i.characterId),
        ),
      );
    compute();
    window.addEventListener(UNIVERSE_VISITED_EVENT, compute);
    return () => window.removeEventListener(UNIVERSE_VISITED_EVENT, compute);
  }, [items]);

  // 성좌 확대(핀치)·이동(팬) — 커스텀 터치 제스처
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const gestureRef = useRef<ConstellationGesture | null>(null);
  const draggedRef = useRef(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const MIN = 0.5; // 1 미만으로도 축소 → 전체 지도를 한눈에
    const MAX = 3.5;

    // 레이아웃(캔버스 크기) 변경 시 변환 초기화
    transformRef.current = { scale: 1, tx: 0, ty: 0 };
    gestureRef.current = null;
    if (canvasRef.current)
      canvasRef.current.style.transform = "translate3d(0,0,0) scale(1)";

    const apply = () => {
      const cv = canvasRef.current;
      if (!cv) return;
      const { scale, tx, ty } = transformRef.current;
      cv.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    };
    // 넉넉한 이동 범위 (화면 가장자리까지 자유롭게 끌어볼 수 있게)
    const bounds = () => {
      const { scale } = transformRef.current;
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      return {
        maxX: Math.max(vw * 0.5, (canvas * scale - vw) / 2 + vw * 0.45),
        maxY: Math.max(vh * 0.5, (canvas * scale - vh) / 2 + vh * 0.45),
      };
    };
    // 경계 너머는 저항을 주며 끌리는 고무줄 효과
    const rubber = (val: number, max: number) =>
      val > max ? max + (val - max) * 0.3 : val < -max ? -max + (val + max) * 0.3 : val;
    // 손을 떼면 경계 안으로 부드럽게 복귀
    const settle = () => {
      const { maxX, maxY } = bounds();
      const tx = Math.max(-maxX, Math.min(maxX, transformRef.current.tx));
      const ty = Math.max(-maxY, Math.min(maxY, transformRef.current.ty));
      const changed =
        tx !== transformRef.current.tx || ty !== transformRef.current.ty;
      transformRef.current.tx = tx;
      transformRef.current.ty = ty;
      const cv = canvasRef.current;
      if (cv && changed) {
        cv.style.transition = "transform 0.28s cubic-bezier(0.22,1,0.36,1)";
        apply();
        window.setTimeout(() => {
          if (cv) cv.style.transition = "";
        }, 300);
      } else {
        apply();
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      draggedRef.current = false;
      if (canvasRef.current) canvasRef.current.style.transition = "";
      if (e.touches.length === 2) {
        const rect = el.getBoundingClientRect();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        );
        const midX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midY = (t1.clientY + t2.clientY) / 2 - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const { scale, tx, ty } = transformRef.current;
        const px = (midX - cx - tx) / scale + cx;
        const py = (midY - cy - ty) / scale + cy;
        gestureRef.current = {
          type: "pinch",
          startDist: dist,
          startScale: scale,
          px,
          py,
          cx,
          cy,
        };
        e.preventDefault();
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        gestureRef.current = {
          type: "pan",
          startX: t.clientX,
          startY: t.clientY,
          startTx: transformRef.current.tx,
          startTy: transformRef.current.ty,
        };
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.type === "pinch" && e.touches.length === 2) {
        const rect = el.getBoundingClientRect();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        );
        const midX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midY = (t1.clientY + t2.clientY) / 2 - rect.top;
        const newScale = Math.max(
          MIN,
          Math.min(MAX, g.startScale * (dist / g.startDist)),
        );
        transformRef.current.scale = newScale;
        transformRef.current.tx = midX - newScale * (g.px - g.cx) - g.cx;
        transformRef.current.ty = midY - newScale * (g.py - g.cy) - g.cy;
        apply();
        draggedRef.current = true;
        e.preventDefault();
      } else if (g.type === "pan" && e.touches.length === 1) {
        const t = e.touches[0];
        if (
          Math.abs(t.clientX - g.startX) > 4 ||
          Math.abs(t.clientY - g.startY) > 4
        ) {
          draggedRef.current = true;
        }
        const { maxX, maxY } = bounds();
        transformRef.current.tx = rubber(g.startTx + (t.clientX - g.startX), maxX);
        transformRef.current.ty = rubber(g.startTy + (t.clientY - g.startY), maxY);
        apply();
        if (draggedRef.current) e.preventDefault();
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        settle();
        gestureRef.current = null;
      } else if (
        e.touches.length === 1 &&
        gestureRef.current?.type === "pinch"
      ) {
        const t = e.touches[0];
        gestureRef.current = {
          type: "pan",
          startX: t.clientX,
          startY: t.clientY,
          startTx: transformRef.current.tx,
          startTy: transformRef.current.ty,
        };
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [canvas]);

  const starColor = c.isDark ? "rgba(255,255,255,0.55)" : "rgba(75,0,204,0.16)";
  const userAvatar = user?.profileImageUrl
    ? getAssetUrl(user.profileImageUrl, "large")
    : "";

  // 다른 곳(컬렉션/충전 등)과 동일한 가입 보상 안내 모달 → 확인 시 로그인 모달
  const showSignupModal = () => {
    openConfirm({
      title: t("profile.login_required_title"),
      subtitle: t("profile.signup_subtitle"),
      infoContent: <SignupRewardInfo />,
      cancelText: t("profile.cancel"),
      confirmText: t("profile.signup_cta"),
      confirmIcon: true,
      onConfirm: () => openLoginModal(),
    });
  };

  return (
    <Box
      sx={{ position: "absolute", inset: 0, overflow: "hidden", bgcolor: c.bg }}
    >
      {/* 분위기용 라디얼 글로우 (브랜드 액센트) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: c.isDark
            ? "radial-gradient(120% 80% at 15% 8%, rgba(124,58,237,0.22) 0%, rgba(124,58,237,0) 55%), radial-gradient(110% 70% at 90% 95%, rgba(254,55,91,0.18) 0%, rgba(254,55,91,0) 55%)"
            : "radial-gradient(120% 80% at 15% 8%, rgba(124,58,237,0.12) 0%, rgba(124,58,237,0) 55%), radial-gradient(110% 70% at 90% 95%, rgba(254,55,91,0.10) 0%, rgba(254,55,91,0) 55%)",
        }}
      />

      {/* 별 */}
      {STARS.map((st, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0.2 }}
          animate={{ opacity: [0.2, 0.85, 0.2] }}
          transition={{
            duration: 3 + (i % 4),
            repeat: Infinity,
            delay: st.d,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            top: st.top,
            left: st.left,
            width: st.s,
            height: st.s,
            borderRadius: "50%",
            backgroundColor: starColor,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* 성좌 영역 (손가락으로 확대·이동) */}
      <Box
        ref={viewportRef}
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          touchAction: "none",
          cursor: "grab",
        }}
      >
        <div
          ref={canvasRef}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: canvas,
            height: canvas,
            marginLeft: -center,
            marginTop: -center,
            transformOrigin: "center center",
            willChange: "transform",
          }}
        >
          {/* 연결선 — 원(아바타) 중심에 정확히 연결 */}
          {positions.length > 0 && (
            <svg
              width={canvas}
              height={canvas}
              viewBox={`0 0 ${canvas} ${canvas}`}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                pointerEvents: "none",
              }}
            >
              {positions.map((p, i) => (
                <motion.line
                  key={p.item.characterId}
                  x1={center}
                  y1={center}
                  x2={p.x}
                  y2={p.y}
                  stroke={p.stage.color}
                  strokeWidth={p.item.isGirlfriend ? 1.8 : 1.3}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{
                    pathLength: 1,
                    opacity: p.item.isGirlfriend ? 0.85 : 0.6,
                  }}
                  transition={{
                    duration: 0.6,
                    delay: 0.15 + i * 0.04,
                    ease: "easeOut",
                  }}
                />
              ))}
            </svg>
          )}

          {/* 중심 — 나 (인연이 있을 때만; 빈 상태에선 거대한 오브가 어색해서 숨김) */}
          {items.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: center,
                top: center,
                transform: "translate(-50%, -50%)",
              }}
            >
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 220,
                  damping: 18,
                  delay: 0.05,
                }}
                style={{ position: "relative" }}
              >
                <motion.div
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{
                    duration: 3.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${c.primary} 0%, #FE375B 100%)`,
                    boxShadow:
                      "0 0 0 6px rgba(124,58,237,0.16), 0 8px 28px rgba(254,55,91,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {userAvatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={userAvatar}
                      alt={t("galaxy.me")}
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Icon name="icMypage" size={38} color="#fff" />
                  )}
                </motion.div>
                <Box
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    px: "10px",
                    py: "2px",
                    borderRadius: "999px",
                    bgcolor: c.bgInset,
                    border: `1px solid ${c.border}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fg }}>
                    {t("galaxy.me")}
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          )}

          {/* 캐릭터 노드 — 탭하면 상세 화면으로 push */}
          {positions.map((p, i) => {
            const name = p.item.name
              ? getLocalizedText(p.item.name, language)
              : "";
            const avatar = p.item.profileImagePath
              ? getAssetUrl(p.item.profileImagePath, "large")
              : "";
            return (
              <Box
                key={p.item.characterId}
                sx={{
                  position: "absolute",
                  left: p.x,
                  top: p.y,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 16,
                    delay: 0.2 + i * 0.05,
                  }}
                  style={{ position: "relative" }}
                >
                  <ButtonBase
                    onClick={() => {
                      if (draggedRef.current) return;
                      // 뮤즈 탭 → 기억 우주로 바로 진입 (기존 상세 시트는 우주 화면 안에서 제공)
                      push(`/muse-universe/${p.item.characterId}`);
                    }}
                    aria-label={name || t("galaxy.aria.character")}
                    sx={{
                      display: "block",
                      width: p.size,
                      height: p.size,
                      borderRadius: "50%",
                      boxShadow: `0 0 0 2px ${p.stage.color}, 0 0 18px ${p.stage.color}66`,
                      overflow: "visible",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        overflow: "hidden",
                        bgcolor: c.bgInset,
                      }}
                    >
                      {avatar ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={avatar}
                          alt={name}
                          draggable={false}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon
                            name="icHeartFilled"
                            size={16}
                            color={p.stage.color}
                          />
                        </Box>
                      )}
                    </Box>
                    {p.item.isGirlfriend && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #FF6B8A 0%, #FE375B 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 6px rgba(254,55,91,0.5)",
                        }}
                      >
                        <Icon name="icHeartFilled" size={10} color="#fff" />
                      </Box>
                    )}
                    {/* 기억 우주 NEW — 새 기억/첫 진입 유도 */}
                    {universeNewIds.has(p.item.characterId) && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -7,
                          left: -10,
                          px: "5px",
                          py: "1px",
                          borderRadius: "6px",
                          bgcolor: "#FF3B30",
                          boxShadow: "0 2px 8px rgba(255,59,48,0.6)",
                          pointerEvents: "none",
                          animation: "galaxyNewPulse 1.6s ease-in-out infinite",
                          "@keyframes galaxyNewPulse": {
                            "0%, 100%": { transform: "scale(1)" },
                            "50%": { transform: "scale(1.12)" },
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: "0.04em", lineHeight: 1.5 }}>
                          NEW
                        </Typography>
                      </Box>
                    )}
                  </ButtonBase>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 5px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      maxWidth: 80,
                      px: "7px",
                      py: "1px",
                      borderRadius: "999px",
                      pointerEvents: "none",
                      bgcolor: c.isDark
                        ? "rgba(0,0,0,0.35)"
                        : "rgba(255,255,255,0.75)",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TEMP_TEXT_BOLD_XS,
                        color: c.fg,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            );
          })}
        </div>
      </Box>

      {/* 헤더 (뒤로 + 제목) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 3,
          px: "12px",
          pt: "calc(env(safe-area-inset-top, 0px) + 12px)",
          pb: "12px",
          pointerEvents: "none",
          display: "flex",
          alignItems: "flex-start",
          gap: "4px",
        }}
      >
        <ButtonBase
          onClick={onBackPress}
          aria-label={t("galaxy.backToChat")}
          sx={{
            pointerEvents: "auto",
            width: 40,
            height: 40,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "-4px",
          }}
        >
          <Icon name="icChevronLeft" size={26} color={c.fg} />
        </ButtonBase>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...TEMP_TITLE_EXTRABOLD_XL, color: c.fg }}>
            {t("galaxy.myWorld")}
          </Typography>
          <Typography
            sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLow, mt: "2px" }}
          >
            {items.length > 0
              ? f(t("galaxy.connectedCount"), [items.length])
              : t("galaxy.mapSubtitle")}
          </Typography>
        </Box>
      </Box>

      {/* 로딩 / 빈 상태 */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fgLow }}>
            {t("galaxy.loading")}
          </Typography>
        </Box>
      )}
      {!isLoading && items.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            px: "40px",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Icon name="icHeartOutlined" size={38} color={c.fgLower} />
          <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg }}>
            {t("galaxy.emptyTitle")}
          </Typography>
          <Typography
            sx={{
              ...TEMP_TEXT_SEMIBOLD_M,
              color: c.fgLow,
              whiteSpace: "pre-line",
            }}
          >
            {isLoggedIn
              ? t("galaxy.emptyLoggedIn")
              : t("galaxy.emptyLoggedOut")}
          </Typography>
          {!isLoggedIn && (
            <ButtonBase
              onClick={showSignupModal}
              sx={{
                pointerEvents: "auto",
                mt: "6px",
                px: "24px",
                py: "13px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)",
                boxShadow: "0 8px 22px rgba(124,58,237,0.4)",
              }}
            >
              <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#fff" }}>
                {t("galaxy.loginCta")}
              </Typography>
            </ButtonBase>
          )}
        </Box>
      )}

      {/* 우측 하단 — [관계 도감(표)] + [채팅으로 돌아가기] */}
      <Box
        sx={{
          position: "absolute",
          right: "16px",
          bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {items.length > 0 && (
          <ButtonBase
            onClick={() => push("/asset-collection")}
            aria-label={t("galaxy.album.title")}
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)",
              boxShadow: "0 8px 22px rgba(124,58,237,0.4)",
            }}
          >
            <PhotoLibraryRoundedIcon sx={{ fontSize: 23, color: "#fff" }} />
          </ButtonBase>
        )}
        {items.length > 0 && (
          <ButtonBase
            onClick={() => push("/relationship-collection")}
            aria-label={t("galaxy.aria.collection")}
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)",
              boxShadow: "0 8px 22px rgba(124,58,237,0.4)",
            }}
          >
            <TableRowsRoundedIcon sx={{ fontSize: 23, color: "#fff" }} />
          </ButtonBase>
        )}
        <ButtonBase
          onClick={onBackPress}
          aria-label={t("galaxy.backToChat")}
          sx={{
            height: 52,
            px: "18px",
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            gap: "7px",
            bgcolor: c.bgInset,
            border: `1px solid ${c.border}`,
            boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
          }}
        >
          <ChatRoundedIcon sx={{ fontSize: 20, color: c.fg }} />
          <Typography
            sx={{ fontSize: 14, fontWeight: 800, color: c.fg, lineHeight: 1 }}
          >
            {t("galaxy.backToChat")}
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ============================================================
// 2-a) 상세 (속마음) — 바텀시트 본문 (나의 세계에서 캐릭터 탭 시)
// ============================================================

/** 화면 하단에서 가려진(모바일 브라우저 툴바 등) 영역 높이.
 *  네이티브 웹뷰/전체화면이면 0. iOS Safari 등 브라우저면 하단 툴바 높이. */
function measureBottomObscured(): number {
  if (typeof window === "undefined" || !window.visualViewport) return 0;
  // 인앱(네이티브 WebView)은 브라우저 툴바가 없고 하단 safe-area는 CSS 변수로 처리한다.
  // Android WebView는 visualViewport.height가 layout보다 작게 잡혀 가짜 양수가 나오므로
  // (→ 시트 내용이 떠오르고 스크롤이 잘림) 인앱이면 보정 없이 0.
  if (window.ReactNativeWebView) return 0;
  const vv = window.visualViewport;
  const layoutH = document.documentElement.clientHeight;
  return Math.max(0, Math.round(layoutH - (vv.offsetTop + vv.height)));
}

export function RelationshipDetailSheetBody({
  characterId,
  onClose,
}: {
  characterId: string;
  onClose: () => void;
}) {
  const c = useDarkColors();
  const { t, language } = useTranslator();
  const { push } = useStackNavigation();
  const { data, isLoading } = useRelationships();

  // 하단 고정 버튼이 모바일 브라우저 툴바에 가리지 않도록 가려진 높이만큼 띄움
  const [bottomObscured, setBottomObscured] = useState(measureBottomObscured);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setBottomObscured(measureBottomObscured());
    const raf = requestAnimationFrame(update);
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  const item = data?.items.find((i) => i.characterId === characterId) ?? null;
  const name = item?.name ? getLocalizedText(item.name, language) : "";
  const avatar = item?.profileImagePath
    ? getAssetUrl(item.profileImagePath, "large")
    : "";
  const stage = item ? getStage(item.intimacy, item.isGirlfriend) : null;

  // 하드웨어 백/스와이프로 시트만 닫기 (스택 pop보다 우선) — NotificationDrawer와 동일 패턴
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const isClosingRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    isClosingRef.current = false;
    const GUARD = "__relationshipDetailSheetGuard";
    history.pushState({ [GUARD]: true }, "");
    const handler = () => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;
      history.pushState({ [GUARD]: true }, "");
      registerTopPriorityBackInterceptor(handler);
      onCloseRef.current();
    };
    registerTopPriorityBackInterceptor(handler);
    return () => {
      unregisterTopPriorityBackInterceptor(handler);
      if ((history.state as Record<string, unknown> | null)?.[GUARD] === true) {
        registerTopPriorityBackInterceptor(() => {});
        history.back();
      }
    };
  }, []);

  const goChat = () => {
    onClose();
    push(`/chat/${characterId}`);
  };

  if (isLoading || !item || !stage) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: "60px",
          px: "24px",
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fgLow }}>
          {isLoading ? t("galaxy.loadingData") : t("galaxy.notFound")}
        </Typography>
      </Box>
    );
  }

  return (
    // pb = 모바일 브라우저 툴바에 가려진 높이 → 전체 내용을 보이는 영역 위로 끌어올려 스크롤 가능하게
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        pb: `${bottomObscured}px`,
      }}
    >
      {/* 스크롤 영역 (정보) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: "24px",
          pt: "4px",
          pb: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
        }}
      >
        {/* 아바타 */}
        <Box sx={{ position: "relative", width: 110, height: 110, mt: "4px" }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              overflow: "hidden",
              bgcolor: c.bgInset,
              boxShadow: `0 0 0 3px ${stage.color}, 0 0 30px ${stage.color}55`,
            }}
          >
            {avatar && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={avatar}
                alt={name}
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </Box>
          {item.isGirlfriend && (
            <Box
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #FF6B8A 0%, #FE375B 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid ${c.bg}`,
                boxShadow: "0 2px 8px rgba(254,55,91,0.5)",
              }}
            >
              <Icon name="icHeartFilled" size={14} color="#fff" />
            </Box>
          )}
        </Box>

        {/* 이름 + 단계 칩을 한 줄로 모아 공간 절약 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg }}>
              {name}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "10px",
                height: 24,
                borderRadius: "999px",
                background: `${stage.color}22`,
                border: `1px solid ${stage.color}55`,
              }}
            >
              <Icon name="icHeartFilled" size={10} color={stage.color} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: stage.color,
                  lineHeight: 1,
                }}
              >
                {t(stage.label)}
              </Typography>
            </Box>
          </Box>
          {item.displayAccountId && (
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLower }}>
              @{item.displayAccountId}
            </Typography>
          )}
        </Box>

        {/* 친밀도 게이지 */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
              {item.intimacyLabel?.trim() || t("gacha.intimacy")}
            </Typography>
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: stage.color }}>
              {item.intimacy}°C
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              height: 10,
              borderRadius: "999px",
              bgcolor: c.bgInset,
              overflow: "hidden",
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${Math.min(100, Math.max(0, item.intimacy))}%`,
              }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                borderRadius: 999,
                background: `linear-gradient(90deg, ${stage.color}99 0%, ${stage.color} 100%)`,
              }}
            />
          </Box>
        </Box>

        {/* 속마음 */}
        <Box
          sx={{
            width: "100%",
            borderRadius: "16px",
            p: "16px 18px",
            bgcolor: c.bgInset,
            border: `1px solid ${c.border}`,
          }}
        >
          <Typography
            sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fg, lineHeight: 1.6 }}
          >
            {item.impression
              ? item.impression
              : t("galaxy.impressionFallback")}
          </Typography>
        </Box>
      </Box>

      {/* 하단 고정 버튼: [닫기] [대화하러 가기] — 가려진 높이는 루트 pb가 처리 */}
      <Box
        sx={{
          flexShrink: 0,
          px: "24px",
          pt: "12px",
          pb: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
          display: "flex",
          gap: "10px",
        }}
      >
        <ButtonBase
          onClick={onClose}
          aria-label={t("discovery.close")}
          sx={{
            flex: "0 0 30%",
            bgcolor: c.bgInset,
            border: `1px solid ${c.border}`,
            borderRadius: "16px",
            py: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>
            {t("discovery.close")}
          </Typography>
        </ButtonBase>
        <ButtonBase
          onClick={goChat}
          sx={{
            flex: 1,
            bgcolor: c.primary,
            borderRadius: "16px",
            py: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#fff" }}>
            {t("galaxy.goChat")}
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
}

// ============================================================
// 2) 상세 (속마음) — 스택 스크린
// ============================================================
export function RelationshipDetail({
  characterId,
  onBackPress,
}: {
  characterId: string;
  onBackPress: () => void;
}) {
  const c = useDarkColors();
  const { t, language } = useTranslator();
  const { push } = useStackNavigation();
  const { data, isLoading } = useRelationships();

  const item = data?.items.find((i) => i.characterId === characterId) ?? null;
  const name = item?.name ? getLocalizedText(item.name, language) : "";
  const avatar = item?.profileImagePath
    ? getAssetUrl(item.profileImagePath, "large")
    : "";
  const stage = item ? getStage(item.intimacy, item.isGirlfriend) : null;

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: c.bg,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 헤더 (뒤로 꺽쇠) */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          pt: "calc(env(safe-area-inset-top, 0px) + 12px)",
          pb: "10px",
        }}
      >
        <ButtonBase
          onClick={onBackPress}
          aria-label={t("galaxy.aria.backToWorld")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="icChevronLeft" size={26} color={c.fg} />
        </ButtonBase>
        <Typography
          sx={{
            ...TEMP_TEXT_BOLD_L,
            color: c.fg,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Typography>
      </Box>

      {isLoading || !item || !stage ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fgLow }}>
            {isLoading ? t("galaxy.loadingData") : t("galaxy.notFound")}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: "24px",
            pb: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 24px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "18px",
          }}
        >
          {/* 아바타 */}
          <Box
            sx={{ position: "relative", width: 120, height: 120, mt: "8px" }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                bgcolor: c.bgInset,
                boxShadow: `0 0 0 3px ${stage.color}, 0 0 30px ${stage.color}55`,
              }}
            >
              {avatar && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={avatar}
                  alt={name}
                  draggable={false}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            {item.isGirlfriend && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #FF6B8A 0%, #FE375B 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${c.bg}`,
                  boxShadow: "0 2px 8px rgba(254,55,91,0.5)",
                }}
              >
                <Icon name="icHeartFilled" size={15} color="#fff" />
              </Box>
            )}
          </Box>

          {/* 이름 + 단계 + @id */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "12px",
                height: 26,
                borderRadius: "999px",
                background: `${stage.color}22`,
                border: `1px solid ${stage.color}55`,
              }}
            >
              <Icon name="icHeartFilled" size={11} color={stage.color} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: stage.color,
                  lineHeight: 1,
                }}
              >
                {t(stage.label)}
              </Typography>
            </Box>
            {item.displayAccountId && (
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLower }}>
                @{item.displayAccountId}
              </Typography>
            )}
          </Box>

          {/* 친밀도 게이지 */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
                {item.intimacyLabel?.trim() || t("gacha.intimacy")}
              </Typography>
              <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: stage.color }}>
                {item.intimacy}°C
              </Typography>
            </Box>
            <Box
              sx={{
                position: "relative",
                height: 10,
                borderRadius: "999px",
                bgcolor: c.bgInset,
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: `${Math.min(100, Math.max(0, item.intimacy))}%`,
                }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${stage.color}99 0%, ${stage.color} 100%)`,
                }}
              />
            </Box>
          </Box>

          {/* 속마음 */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "16px",
              p: "16px 18px",
              bgcolor: c.bgInset,
              border: `1px solid ${c.border}`,
            }}
          >
            <Typography
              sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fg, lineHeight: 1.6 }}
            >
              {item.impression
                ? item.impression
                : t("galaxy.impressionFallback")}
            </Typography>
          </Box>

          {/* 대화하러 가기 */}
          <ButtonBase
            onClick={() => push(`/chat/${item.characterId}`)}
            sx={{
              width: "100%",
              mt: "auto",
              bgcolor: c.primary,
              borderRadius: "16px",
              py: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#fff" }}>
              {t("galaxy.goChat")}
            </Typography>
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}

// ============================================================
// 3) 나의 관계 도감 — 컬렉션 표 (스택 스크린)
// ============================================================
export function RelationshipCollection({
  onBackPress,
}: {
  onBackPress: () => void;
}) {
  const c = useDarkColors();
  const { t, f, language } = useTranslator();
  const [copied, setCopied] = useState(false);
  const { data } = useRelationships();
  const items = data?.items ?? EMPTY_ITEMS;

  const ranked = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          b.intimacy - a.intimacy ||
          (b.chatCount ?? 0) - (a.chatCount ?? 0) ||
          Number(b.isGirlfriend) - Number(a.isGirlfriend),
      ),
    [items],
  );
  const total = items.length;
  const maxedCount = items.filter((i) => i.intimacy >= 100).length;
  const intimateCount = items.filter(
    (i) => i.isGirlfriend || i.intimacy >= 71,
  ).length;

  const stats = [
    { label: t("galaxy.stats.total"), value: `${total}` },
    { label: t("galaxy.stats.maxed"), value: `${maxedCount}` },
    { label: t("galaxy.stats.intimate"), value: `${intimateCount}` },
  ];

  const distribution = STAGE_DEFS.map((d) => ({
    ...d,
    count: items.filter(
      (it) => getStage(it.intimacy, it.isGirlfriend).label === d.label,
    ).length,
  })).filter((d) => d.count > 0);

  const handleShare = async () => {
    const maxedText = maxedCount > 0 ? f(t("galaxy.share.maxedSuffix"), [maxedCount]) : "";
    const text = f(t("galaxy.share.text"), [total, intimateCount, maxedText]);
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: t("galaxy.collection.title"), text });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      /* 공유 취소 등은 무시 */
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: c.bg,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "16px",
          pt: "calc(env(safe-area-inset-top, 0px) + 14px)",
          pb: "12px",
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <ButtonBase
          onClick={onBackPress}
          aria-label={t("galaxy.aria.backToWorld")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-6px",
          }}
        >
          <Icon name="icChevronLeft" size={26} color={c.fg} />
        </ButtonBase>
        <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg }}>
          {t("galaxy.collection.title")}
        </Typography>
        <ButtonBase
          onClick={handleShare}
          aria-label={t("galaxy.aria.share")}
          sx={{
            width: 38,
            height: 38,
            borderRadius: "999px",
            bgcolor: c.bgInset,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IosShareRoundedIcon sx={{ fontSize: 18, color: c.fg }} />
        </ButtonBase>
      </Box>

      {/* 본문 (스크롤) */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: "16px",
          py: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* 통계 (콤팩트) */}
        <Box
          sx={{
            borderRadius: "16px",
            p: "14px 16px",
            background: "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)",
            boxShadow: "0 10px 28px rgba(124,58,237,0.3)",
          }}
        >
          <Box sx={{ display: "flex" }}>
            {stats.map((s, i) => (
              <Box
                key={s.label}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  borderLeft:
                    i === 0 ? "none" : "1px solid rgba(255,255,255,0.25)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: "#fff",
                    lineHeight: 1.1,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.85)",
                    mt: "3px",
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* 단계별 누적 분포 */}
        {distribution.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                height: 12,
                borderRadius: "999px",
                overflow: "hidden",
                bgcolor: c.bgInset,
              }}
            >
              {distribution.map((d) => (
                <Box
                  key={d.label}
                  sx={{
                    width: `${(d.count / total) * 100}%`,
                    bgcolor: d.color,
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "7px 14px",
                mt: "10px",
              }}
            >
              {distribution.map((d) => (
                <Box
                  key={d.label}
                  sx={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: d.color,
                    }}
                  />
                  <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLow }}>
                    {t(d.label)}
                  </Typography>
                  <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fg }}>
                    {d.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* 조밀한 아바타 그리드 (친밀도순) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))",
            gap: "16px 8px",
          }}
        >
          {ranked.map((item, idx) => {
            const name = item.name ? getLocalizedText(item.name, language) : "";
            const avatar = item.profileImagePath
              ? getAssetUrl(item.profileImagePath, "large")
              : "";
            const stage = getStage(item.intimacy, item.isGirlfriend);
            const medal = MEDAL_COLORS[idx];
            return (
              <Box
                key={item.characterId}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: 0,
                }}
              >
                <Box sx={{ position: "relative", width: 54, height: 54 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      overflow: "hidden",
                      bgcolor: c.bgInset,
                      boxShadow: `0 0 0 2px ${stage.color}`,
                    }}
                  >
                    {avatar && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={avatar}
                        alt={name}
                        draggable={false}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -5,
                      left: "50%",
                      transform: "translateX(-50%)",
                      px: "6px",
                      height: 16,
                      borderRadius: "999px",
                      bgcolor: stage.color,
                      display: "flex",
                      alignItems: "center",
                      border: `1.5px solid ${c.bg}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 900,
                        color: "#fff",
                        lineHeight: 1,
                      }}
                    >
                      {item.intimacy}°
                    </Typography>
                  </Box>
                  {medal && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: medal,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1.5px solid ${c.bg}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 900,
                          color: "#fff",
                          lineHeight: 1,
                        }}
                      >
                        {idx + 1}
                      </Typography>
                    </Box>
                  )}
                  {item.isGirlfriend && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        left: -4,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #FF6B8A 0%, #FE375B 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1.5px solid ${c.bg}`,
                      }}
                    >
                      <Icon name="icHeartFilled" size={9} color="#fff" />
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{
                    ...TEMP_TEXT_BOLD_XS,
                    color: c.fg,
                    maxWidth: "100%",
                    mt: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                  }}
                >
                  {name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* 워터마크 (자랑용) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            py: "8px",
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 13, color: c.fgLower }} />
          <Typography
            sx={{
              ...TEMP_TEXT_BOLD_XS,
              color: c.fgLower,
              letterSpacing: "0.5px",
            }}
          >
            {t("galaxy.collection.watermark")}
          </Typography>
        </Box>
      </Box>

      {/* 복사 토스트 */}
      <AnimatePresence>
        {copied && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 24px)",
            }}
          >
            <Box
              sx={{
                px: "16px",
                py: "10px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)",
                boxShadow: "0 6px 18px rgba(124,58,237,0.4)",
              }}
            >
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: "#fff" }}>
                {t("galaxy.share.copied")}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
